import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, useTheme, Tabs, Tab, IconButton, Drawer } from '@mui/material';
import Divider from '@mui/material/Divider';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MenuIcon from '@mui/icons-material/Menu';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EventIcon from '@mui/icons-material/Event';
import LaptopIcon from '@mui/icons-material/Laptop';
import MessageIcon from '@mui/icons-material/Message';
import { tokens } from '../../theme';
import { useAuth } from '../../AuthContext';
import Profile from './Profile';
import Leave from './Leave';
import Borrow from './Borrow';
import Messages from './Messages';
import { styled } from '@mui/material/styles';
import Badge from '@mui/material/Badge';
import Avatar from '@mui/material/Avatar';
import Home from './Home';
import notificationSound from "../../assets/notification/newnotification.mp3";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import BroadcastOnHomeIcon from "@mui/icons-material/BroadcastOnHome";
import SchoolIcon from '@mui/icons-material/School';
import AddTaskIcon from '@mui/icons-material/AddTask';

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

const UserDashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { userID, getData, addData, userRole } = useAuth();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeUser, setActiveUser] = useState([]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setMobileOpen(false); // Close drawer on tab selection in mobile view
  };

  useEffect(() => {
    async function fetchLeaves() {
      try {
        const response = await getData('proccess.php', 'staffs', userID);
        if (response.success) {
          setActiveUser(response.data[0]);
        }
      } catch (err) {
        console.error("Fetch error:", err);
      }
    }
    fetchLeaves();
  }, [userID, getData]);

  // ############################################################################################
  
    const [messageCount, setMessageCount] = useState(0);
    const [readBy, setReadBy] = useState([]);
    const [messages, setMessages] = useState([]);
    const [showMessages, setShowMessages] = useState(false);
    const [userInteracted, setUserInteracted] = useState(false);
    const audioRef = useRef(null);
    const lastPlayedMessageIds = useRef(new Set());


  useEffect(() => {
      const handleUserInteraction = () => {
        setUserInteracted(true);
        window.removeEventListener("click", handleUserInteraction);
      };
  
      window.addEventListener("click", handleUserInteraction);
      return () => window.removeEventListener("click", handleUserInteraction);
    }, []);
  
  useEffect(() => {
    audioRef.current = new Audio(notificationSound);
    audioRef.current.volume = 1;

    const fetchMessages = async () => {
      try {
        const responseReadBy = await getData("proccess.php", "readBy_V2");
        setReadBy(responseReadBy.data);

        const response = await getData("proccess.php", "broadcast_V2");
        const newMessages = response.data || [];
        setMessages(newMessages);

        // Check for new unread messages
        const newUnreadMessages = newMessages.filter(
          (bct) => !responseReadBy.data.find(
            (msg) => msg.broadcastId === bct.id && msg.userId === userID
          )
        );

        // Update message count
        setMessageCount(newUnreadMessages.length);

        // Play sound only if there are new unread messages and not already played
        if (newUnreadMessages.length > 0 && !lastPlayedMessageIds.current.has(newUnreadMessages[0].id)) {
          playNotificationSound();
          newUnreadMessages.forEach((msg) => lastPlayedMessageIds.current.add(msg.id));
        }

        const responseMessage = await getData("proccess.php", "messages_V2");
        const newMessagesResponse = responseMessage.data || [];

        const unreadCountNumber = newMessagesResponse.filter(
          (msg) => msg.status === 0 && msg.receiver === userID
        ).length;
      } catch (error) {
        console.error("Failed to fetch messages from broadcast_V2 table:", error);
      }
    };

    fetchMessages();
    const intervalId = setInterval(fetchMessages, 10000);

    return () => {
      clearInterval(intervalId);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [userID, getData, userInteracted]);
  
  const markAsRead = async (messageId) => {
    try {
      const isRead = readBy.find(user => user.userId === userID && user.broadcastId === messageId);
      if(!isRead){
        const response = await addData(
          "proccess.php",
          "readBy_V2",
          { 
            broadcastId: messageId,
            userId: userID
          }
        );
        if (response.success) {
          //console.log(response.message);
          // Update local state to reflect the read status
          setReadBy(response.data);
          // Update message count
          setMessageCount((prevCount) => prevCount - 1);
        } else {
          console.error("Failed to mark message as read:", response.message);
        }
      }

    } catch (error) {
      console.error("Error marking message as read:", error);
    }
  };
  
    const playNotificationSound = () => {
      try {
        const audio = audioRef.current;
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            if (userInteracted) {
              console.warn("Audio playback prevented:", error);
            }
          });
        }
      } catch (error) {
        console.error("Audio playback failed:", error);
      }
    };
  
    const toggleMessages = () => {
      setShowMessages((prev) => !prev);
    };
  
    const formatTime = (timeString) => {
      if (timeString === "Just now") return timeString;
      try {
        const time = new Date(timeString);
        if (isNaN(time.getTime())) return "Invalid time";
        return time.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch {
        return "Invalid time";
      }
    };
  
    const getColor = (path, fallback) => {
      try {
        return path.split(".").reduce((o, i) => o[i], colors) || fallback;
      } catch {
        return fallback;
      }
    };
  // ############################################################################################

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawerContent = (
    <Box
      sx={{
        width: { xs: '250px', sm: '200px' },
        backgroundColor: colors.primary[500],
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto', // Enable scrolling
        overflowX: 'hidden',
      }}
    >
      <Box sx={{ textAlign: 'center', marginBottom: '1rem' }}>
        <Box bgcolor={'transparent'} display={'flex'} alignItems={'center'} justifyContent={'center'} padding={'1rem'}>
          <Avatar
            alt="Remy Sharp"
            src={require(`../../assets/profiles/logo-blog.png`)}
            sx={{ width: 70, height: 70 }}
          />
        </Box>
        <Typography variant="h4" color={colors.dark[400]} fontWeight="bold">
          {activeUser.LastName} {activeUser.FirstName}
        </Typography>
        <Typography variant="h6" color={colors.dark[400]} fontWeight="bold">
          {activeUser.email}
        </Typography>
      </Box>
      <Tabs
        orientation="vertical"
        value={tabValue}
        onChange={handleTabChange}
        sx={{
          '& .MuiTab-root': {
            color: colors.dark[400],
            fontSize: { xs: '0.8rem', sm: '0.7rem' }, // Reduced font size
            textTransform: 'none',
            alignItems: 'flex-start',
            padding: '8px 12px', // Reduced padding
            minHeight: '36px', // Smaller tab height
            '& .MuiTab-iconWrapper': {
              fontSize: '1rem', // Smaller icon size
              marginRight: '8px',
            },
            '&.Mui-selected': {
              backgroundColor: colors.blue[400],
              color: colors.primary[100],
            },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: colors.blue[400],
            width: '4px',
          },
        }}
      >
        <Tab icon={<PersonOutlineIcon />} label="DASHBOARD" />
        <Tab icon={<EventIcon />} label="Leave Requests" />
        <Tab icon={<LaptopIcon />} label="Borrow Requests" />
        <Tab icon={<MessageIcon />} label="Messages" />
        <Tab icon={<PersonOutlineIcon />} label="Profile" />
      </Tabs>
      <Box sx={{ marginTop: 'auto', padding: '20px' }}>
        {userRole !== 'user' ? (
          <Button
            variant="contained"
            onClick={() => navigate(-1)}
            sx={{
              backgroundColor: colors.red[400],
              '&:hover': { backgroundColor: colors.red[500] },
              width: '100%',
            }}
          >
            Take Me Back
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => navigate('/user/dashboard/signout')}
            sx={{
              backgroundColor: colors.red[400],
              '&:hover': { backgroundColor: colors.red[500] },
              width: '100%',
            }}
          >
            Log Out
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <Box display="flex" minHeight="100vh">
      <ToastContainer position="top-right" autoClose={5000} />

      {/* Hamburger Menu for Mobile */}
      <IconButton
        color="inherit"
        aria-label="open drawer"
        edge="start"
        onClick={handleDrawerToggle}
        sx={{
          display: { sm: 'none' },
          position: 'fixed',
          top: 10,
          left: 20,
          zIndex: 1200,
          padding: '.5rem',
          background: colors.primary[300],
        }}
      >
        <MenuIcon sx={{ color: colors.dark[400] }} />
      </IconButton>

      {/* Sidebar for Desktop */}
      <Box
        sx={{
          width: { sm: '200px' },
          backgroundColor: colors.primary[500],
          position: { sm: 'fixed' },
          height: '100vh',
          borderRight: { sm: `1px solid ${theme.palette.divider}` },
          display: { xs: 'none', sm: 'flex' },
          flexDirection: 'column',
          paddingTop: '20px',
          overflowY: 'auto', // Enable scrolling
          overflowX: 'hidden',
        }}
      >
        {drawerContent}
      </Box>

      {/* Drawer for Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better performance on mobile
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: '250px',
            backgroundColor: colors.primary[500],
            overflowY: 'auto', // Enable scrolling
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box
        sx={{
          marginLeft: { xs: '0', sm: '200px' },
          flexGrow: 1,
          padding: { xs: '10px', sm: '10px' },
          backgroundColor: colors.primary[400],
          width: { xs: '100%', sm: 'calc(100% - 200px)' },
        }}
      >
        <Box>
          <Box mb="20px" display="flex" justifyContent="space-between" alignItems="center">
            <Typography paddingLeft={'3rem'} variant={{xs: 'h4', sm: 'h3'}} color={colors.dark[400]} fontWeight="bold">
              Hello {activeUser.LastName} !!!
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="center">
                        <IconButton onClick={() => navigate("/user/dashboard/broadcast")}>
                          <BroadcastOnHomeIcon sx={{fontSize: { xs: '.8rem', sm: '1rem', md: '1.5rem'}}}/>
                        </IconButton>
                        <Typography
                          sx={{
                            display: {xs: 'none', sm: 'none', md: 'flex'},
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          Broadcast Message
                        </Typography>
            </Box>

                      <Box display="flex" alignItems="center" justifyContent="center">
                          <IconButton onClick={() => navigate("/tasks/dashboard")}
                          >
                            <AddTaskIcon sx={{fontSize: { xs: '.8rem', sm: '1rem', md: '1.5rem'}}}/>
                          </IconButton>
                          <Typography
                            sx={{
                              display: {xs: 'none', sm: 'none', md: 'flex'},
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            Task Manager
                          </Typography>
                        </Box>

            {userRole === "receptionist" || userRole === 'user' ? (
            <Box display="flex" alignItems="center" justifyContent="center">
                        <IconButton onClick={() => navigate("/user/dashboard/classmanagement")}>
                          <SchoolIcon sx={{fontSize: { xs: '.8rem', sm: '1rem', md: '1.5rem'}}}/>
                        </IconButton>
                        <Typography
                          sx={{
                            display: {xs: 'none', sm: 'none', md: 'flex'},
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          Class Management
                        </Typography>
            </Box>) : null}
            <Box display="flex" justifyContent="center" alignItems="center" gap={'1rem'}>
              <Box position="relative">
                        <IconButton onClick={toggleMessages}>
                          <Badge
                            color="error"
                            badgeContent={messageCount}
                            max={99}
                            invisible={messageCount === 0}
                            sx={{
                              "& .MuiBadge-badge": {
                                animation: messageCount > 0 ? "pulse 1.5s infinite" : "none",
                                right: 4,
                                top: 4,
                              },
                            }}
                          >
                            <NotificationsOutlinedIcon sx={{fontSize: { xs: '1.5rem', sm: '1.5rem', md: '1.5rem'}}}/>
                          </Badge>
                        </IconButton>
              
                        {showMessages && (
                          <Box
                            position="absolute"
                            right={0}
                            width={{ xs: '320px', sm: '320px', md: '320px'}}
                            bgcolor={getColor("primary.500", "#1a1a1a")}
                            boxShadow={3}
                            borderRadius="4px"
                            zIndex="tooltip"
                            maxHeight="400px"
                            overflow="auto"
                          >
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              p={2}
                              borderBottom={`1px solid ${getColor("grey.700", "#e0e0e0")}`}
                            >
                              <Typography variant="h6">Messages</Typography>
                            </Box>
              
                            {messages.length === 0 ? (
                              <Box p={2} textAlign="center">
                                <Typography variant="body2">No messages</Typography>
                              </Box>
                            ) : (
                              messages.map(
                                (message) =>
                                  message.status === 0 ? (
                                    <Box
                                      key={message.id}
                                      p={2}
                                      sx={{
                                        borderBottom: `1px solid ${getColor(
                                          "grey.700",
                                          "#e0e0e0"
                                        )}`,
                                        backgroundColor:
                                          message.status === 0
                                            ? getColor("primary.600", "#2a2a2a")
                                            : getColor("primary.500", "#1a1a1a"),
                                        cursor: "pointer",
                                        "&:hover": {
                                          backgroundColor: getColor(
                                            "blueAccent.800",
                                            "#4d4d4e"
                                          ),
                                        },
                                      }}
                                      onClick={() => markAsRead(message.id)}
                                    >
                                      <Typography variant="body1">
                                        {message.message}
                                        {message.status === 0 && " 🔔"}
                                        {message.senderID === userID && " ↗️"}
                                      </Typography>
                                      <Typography variant="caption" color="textSecondary">
                                        {formatTime(message.date_created)}
                                      </Typography>
                                    </Box>
                                  ):(
                                                        <Box
                                                          key={message.id}
                                                          p={2}
                                                          sx={{
                                                            borderBottom: `1px solid ${getColor(
                                                              "grey.700",
                                                              "#e0e0e0"
                                                            )}`,
                                                            backgroundColor:
                                                              message.status === 0
                                                                ? getColor("primary.600", "#2a2a2a")
                                                                : getColor("primary.500", "#1a1a1a"),
                                                            cursor: "pointer",
                                                            "&:hover": {
                                                              backgroundColor: getColor(
                                                                "blueAccent.800",
                                                                "#4d4d4e"
                                                              ),
                                                            },
                                                          }}
                                                          onClick={() => markAsRead(message.id)}
                                                        >
                                                          <Typography variant="body1">
                                                            {message.message}
                                                            {message.status === 0 && " 🔔"}
                                                            {message.senderID === userID && " ↗️"}
                                                          </Typography>
                                                          <Typography variant="caption" color="textSecondary">
                                                            {formatTime(message.date_created)}
                                                          </Typography>
                                                        </Box>
                                                      )
                              )
                            )}
                          </Box>
                        )}
                      </Box>
              <StyledBadge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                variant="dot"
              >
                <Avatar alt="Remy Sharp" src={require(`../../assets/profiles/logo-blog.png`)} />
              </StyledBadge>
            </Box>
          </Box>
          <Divider />
        </Box>
        {tabValue === 0 && <Home userId={userID} />}
        {tabValue === 1 && <Leave userId={userID} />}
        {tabValue === 2 && <Borrow userId={userID} />}
        {tabValue === 3 && <Messages userId={userID} />}
        {tabValue === 4 && <Profile userId={userID} />}
      </Box>
    </Box>
  );
};

export default UserDashboard;